import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Paper, Box, Collapse, IconButton
}  from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useCourseState } from '../../../context/CourseContext';
import provisionService from '../../../services/ProvisionService';
import { getStyles } from './styles';

export type SelectionState = {
    id: string;
    name: string;
    grades: {
      name: number;
      courses: {
        id: string;
        name: string;
      }[];
    }[];
}

export default function AccessTable() {
    const { site, accessSelections } = useCourseState();
    const [schools, setSchools] = useState<any[]>([]);
    const [courses, setCourses] = useState<any[]>([]);
    const [selections, setSelections] = useState([] as SelectionState[]);

    useEffect(() => {
      const fetchSchools = async () => {
        if (site) {
          const data = await provisionService.getSchools(site.id);
          if (isMounted && data) setSchools(data);
        }
      };
      const fetchCourses = async () => {
        if (site) {
          const data = await provisionService.getCourses(site.id);
          if (isMounted) setCourses(data);
        }
      };
        
      let isMounted = true;
      fetchSchools();
      fetchCourses();
      return () => { isMounted = false };
    }, [site]);

    useEffect(() => {
        const parseSelections = () => {

          let newSelections: SelectionState[] = []
          accessSelections?.forEach(selectionString => {
            let orgId: string = '';
            let grade: number = 0;
            let courseId: string = '';
        
            const selectionArray = selectionString.split('|');
            for (const keys of selectionArray) {
              const keyValue = keys.split(':');
        
              switch (keyValue[0]) {
                case 'org':
                  orgId = keyValue[1];
                  break;
                case 'grade':
                  grade = keyValue[1];
                  break;
                case 'course':
                  courseId = keyValue[1];
                  break;
              }
            }
    
            let selection = newSelections.find(s => s.id === orgId);
            if (!selection) {
                selection = {
                    id: orgId,
                    name: getSchoolName(orgId),
                    grades: [],
                }
            }
    
            if (grade > 0) {
                if (selection.grades.findIndex(g => g.name === grade) === -1) {
                    selection.grades.push({
                        name: grade,
                        courses: [],
                    })
                }
                if (courseId) {
                    selection.grades.find(g => g.name === grade)?.courses.push({
                        id: courseId,
                        name: getCourseName(courseId),
                    })
                }
            }

            newSelections = newSelections.filter(s => s.id !== selection?.id);
            newSelections.push(selection);
          })

          setSelections(newSelections);
        }

        const getSchoolName = (schoolId: string): string => {
            const school = schools.find(school => school.id === schoolId);
            if (school) return school.name;
            else return `School ID: ${schoolId}: Name not found`;
        }

        const getCourseName = (courseId: string): string => {
            const course = courses.find(course => course.id === courseId);
            if (course) return course.name
            else return `Course: ${courseId}: Name not found`;
        }
        
        if (schools && schools.length > 0 
            && courses && courses.length > 0
            && accessSelections) {
            parseSelections();
        }

    }, [accessSelections, schools, courses]);

    return (
      <TableContainer component={Paper}>
        <Table aria-label="access table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>School Name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selections.map((selection) => (
              <Row key={selection.id} selection={selection} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

interface RowProps {
    selection: SelectionState;
}

function Row({ selection }: RowProps) {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const styles = getStyles(theme);

  return (
    <React.Fragment>
      <TableRow sx={styles.root}>
        <TableCell>
          { selection.grades.length > 0 &&
            <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          }
        </TableCell>
        <TableCell component="th" scope="row">
          {selection.name}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{
              mx: 3
            }}>
              {selection.grades.sort((a, b) => a.name - b.name).map((grade) => (
                <React.Fragment key={grade.name}>
                  <Typography sx={styles.tableCell}>
                    Grade: {grade.name}
                  </Typography>
                <Box sx={{
                  mx: 4
                }}>
                  { grade.courses &&
                    grade.courses.map((course) => (
                      <Typography key={`${course.id}-${Math.floor(Math.random() * 10) + 1}`} sx={styles.tableCell}>
                        - {course.name}
                      </Typography>
                  ))}
                </Box>
                </React.Fragment>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}
